import { Stack, Typography } from '@mui/material'
import { WorkInstructionControlRoomJobDto } from '@operations/app/api'
import { useTranslate } from '@tolgee/react'
import { EmptyIndicator, JobsIcon, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { WorkInstructionTableDate } from '../atoms/WorkInstructionTableDate'

interface Props {
  jobs: WorkInstructionControlRoomJobDto[]
}
export const WorkInstructionJobs = observer(({ jobs }: Props) => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()

  if (jobs.length === 0) {
    return (
      <Typography
        variant='subtitle2'
        color='secondary'
        textAlign='center'
        padding={theme.customSpacing.s}
      >
        {t('noJobsToDisplay', 'No jobs to display')}
      </Typography>
    )
  }

  return (
    <Stack
      flexDirection='row'
      sx={{
        bgcolor: theme.palette.grey[100],
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack
        sx={{
          position: 'sticky',
          left: 0,
          p: theme.customSpacing.s,
          width: '120px',
          bgcolor: theme.palette.grey[100],
          borderRight: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack flexDirection='row' gap={theme.customSpacing.m} alignItems='center'>
          <JobsIcon fontSize='small' />
          <Typography variant='captionBold'>{t('jobs', 'Jobs')}</Typography>
        </Stack>
      </Stack>

      <Stack gap={theme.customSpacing.m} py={theme.customSpacing.s} ml='20px'>
        <Stack flexDirection='row' gap={theme.customSpacing.l}>
          <Typography variant='captionBold' width='200px'>
            {t('origin', 'Origin')}
          </Typography>
          <Typography variant='captionBold' width='200px'>
            {t('destination', 'Destination')}
          </Typography>
          <Typography variant='captionBold' width='170px'>
            {t('lastEquipment', 'Last equipment')}
          </Typography>
          <Typography variant='captionBold' width='200px'>
            {t('executionTime', 'Execution time')}
          </Typography>
        </Stack>

        {jobs.map((job, index) => (
          <Stack key={job.id} flexDirection='row' gap={theme.customSpacing.l}>
            <Typography
              variant='body2'
              width='200px'
              data-cy={`job-${index}-location-from`}
              sx={{ wordBreak: 'break-word' }}
            >
              {job.fromDescription ?? EmptyIndicator}
            </Typography>
            <Typography
              variant='body2'
              width='200px'
              data-cy={`job-${index}-location-to`}
              sx={{ wordBreak: 'break-word' }}
            >
              {job.toDescription ?? EmptyIndicator}
            </Typography>
            <Typography
              variant='body2'
              width='170px'
              data-cy={`job-${index}-equipment-name`}
              sx={{ wordBreak: 'break-word' }}
            >
              {job.equipmentName ?? EmptyIndicator}
            </Typography>
            <Typography variant='body2' width='200px'>
              <WorkInstructionTableDate date={job.executionTime} isOnSameLine />
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
})
